/**
 * The payments seam, in memory (M7.7).
 *
 * The same arrangement as `portal.mock.ts`: one fixture-backed implementation of the
 * real interface, chosen by `VITE_USE_MOCKS` in `payments-client-context.tsx`, so the
 * whole funnel — card form included — can be walked, screenshotted and tested with no
 * API and, crucially, **no vendor**.
 *
 * Two halves, and both are needed to make that true:
 *
 *  - `createMockPaymentsClient()` is a tiny vault. It answers `config` with a sandbox
 *    answer, mints `PaymentMethodSummary` rows from the fake nonce, and keeps one card
 *    on file so the portal's read/replace/remove cycle is real.
 *  - `mountFakeTokenizer` is the stand-in for accept.blue's iframe. It renders a plain,
 *    labelled, keyboard-reachable box into the same host element and hands back
 *    `nonce-mock…`. It loads **no script** and opens **no iframe**, which is what lets a
 *    test assert that the document contains neither.
 *
 * Nothing here ever sees a card number: the fake form has no PAN field, because a fake
 * PAN in a mock is a real PAN the first time somebody demonstrates the app with their own
 * wallet in their hand (SECURITY.md §8).
 */

import type { PaymentConfig, PaymentMethodSummary } from '@platform/contracts';

import type { PaymentsClient, SaveCardInput } from './payments';
import type { MountTokenizer, MountedTokenizer } from './payments-tokenizer';

/**
 * The sandbox answer, with the real sandbox script URL.
 *
 * It is never fetched — `mountFakeTokenizer` ignores it — but it is the honest value: a
 * mock that answered with a made-up URL would hide a CSP mistake until production.
 */
export const MOCK_PAYMENT_CONFIG: PaymentConfig = Object.freeze({
  enabled: true,
  provider: 'acceptblue',
  publicKey: 'pk_mock_sandbox',
  scriptUrl: 'https://tokenization.sandbox.accept.blue/tokenization/v0.3',
  sandbox: true,
});

export interface MockPaymentsOptions {
  /** Start with a card on file (the portal's usual state). Default: true. */
  seeded?: boolean;
  /** Answer `enabled: false`, the clinic that takes no cards. */
  disabled?: boolean;
  /** Fixed clock, so a seeded `createdAt` is stable in a snapshot. */
  now?: () => Date;
}

const SEEDED_CARD: PaymentMethodSummary = Object.freeze({
  id: '9f1c0c3e-0b0a-4a3e-9f6a-2c6d5f4b1a01',
  brand: 'Visa',
  last4: '4242',
  expMonth: 8,
  expYear: 2028,
  status: 'active',
  isDefault: true,
  createdAt: '2026-06-01T12:00:00.000Z',
});

export function createMockPaymentsClient(options: MockPaymentsOptions = {}): PaymentsClient {
  const now = options.now ?? (() => new Date());
  let onFile: PaymentMethodSummary | null = options.seeded === false ? null : { ...SEEDED_CARD };
  let serial = 0;

  const vault = (input: SaveCardInput): PaymentMethodSummary => {
    serial += 1;
    return {
      id: `9f1c0c3e-0b0a-4a3e-9f6a-2c6d5f4b1a${String(10 + serial).padStart(2, '0')}`,
      /* The fake tokenizer encodes a brand and four digits in the nonce; see below. */
      brand: brandFromNonce(input.nonce),
      last4: last4FromNonce(input.nonce),
      expMonth: input.expiryMonth,
      expYear: input.expiryYear,
      status: 'active',
      isDefault: true,
      createdAt: now().toISOString(),
    };
  };

  return {
    config: () =>
      Promise.resolve(
        options.disabled === true
          ? { enabled: false, provider: null, publicKey: null, scriptUrl: null, sandbox: true }
          : { ...MOCK_PAYMENT_CONFIG },
      ),

    saveDraftCard: (input) => Promise.resolve(vault(input)),
    saveBookingCard: (_bookingId, input) => Promise.resolve(vault(input)),

    getCardOnFile: () => Promise.resolve(onFile === null ? null : { ...onFile }),
    saveCardOnFile: (input) => {
      onFile = vault(input);
      return Promise.resolve({ ...onFile });
    },
    removeCardOnFile: () => {
      onFile = null;
      return Promise.resolve();
    },
  };
}

/* ------------------------------------------------------------------ *
 * The fake hosted form
 * ------------------------------------------------------------------ */

/**
 * `nonce-mockVisa4242` — a brand, four digits, and nothing that could be a card.
 *
 * The shape is the vendor's, not ours: accept.blue's source grammar is
 * `(nonce|tkn|ref)-[A-Za-z0-9]+` with no second hyphen and at most 26 characters, and the
 * platform's adapter enforces it before anything leaves. A prettier `nonce-mock-visa-4242`
 * is refused by the **real** API with `400 VALIDATION_FAILED` on `['nonce']`, which is
 * exactly the wrong lesson for a fixture to teach.
 *
 * The suffix is what lets the mock vault answer with a brand and a last four, so the
 * confirmation panel and the portal's "Visa ending 4242" line are exercised end to end
 * without the vendor.
 */
export const MOCK_NONCE = 'nonce-mockVisa4242';

const MOCK_NONCE_PARTS = /^nonce-mock([A-Za-z]+)(\d{4})$/;

function brandFromNonce(nonce: string): string | null {
  const brand = MOCK_NONCE_PARTS.exec(nonce)?.[1];
  return brand === undefined ? 'Card' : brand.charAt(0).toUpperCase() + brand.slice(1);
}

function last4FromNonce(nonce: string): string | null {
  return MOCK_NONCE_PARTS.exec(nonce)?.[2] ?? null;
}

/**
 * The stand-in for the hosted iframe.
 *
 * It renders a static, labelled panel — not a set of inputs. There is deliberately
 * nothing to type into: the mock exists so the *flow* can be exercised, and a fake card
 * field invites somebody to try a real card in it.
 */
export const mountFakeTokenizer: MountTokenizer = (options) => {
  const host = document.querySelector(options.target);
  if (host === null) return Promise.reject(new Error('The payment form has nowhere to mount.'));

  const panel = document.createElement('div');
  panel.dataset['testid'] = 'fake-tokenizer';
  panel.className = 'card-form-placeholder';
  panel.textContent = 'Test card form — no card details are collected.';
  host.replaceChildren(panel);

  const mounted: MountedTokenizer = {
    getNonceToken: () =>
      Promise.resolve({
        nonce: MOCK_NONCE,
        last4: '4242',
        cardType: 'Visa',
        expiryMonth: 8,
        expiryYear: 2028,
      }),
    destroy: () => {
      host.replaceChildren();
    },
  };
  return Promise.resolve(mounted);
};
